/**
 * Wall Newspaper — printing
 *
 * Two outputs:
 *   1. Multi-page PDF (jsPDF, lazy-loaded). One paper page per sheet, artwork
 *      placed INSIDE the printable margin, with crop marks, sheet labels,
 *      neighbour arrows, a tinted glue band and an optional calibration ruler.
 *   2. ZIP of PNGs — the same tiles as image files.
 *
 * Public API (window.WNPrinting):
 *   - exportPDF({project, contentNodes, options, onProgress}) → Promise<Blob>
 *   - exportPNGZip({project, contentNodes, options, onProgress}) → Promise<Blob>
 *   - tilePreviewURL({project, contentNodes, row, col, options, maxW}) → Promise<dataURL>
 *
 * `contentNodes` is an array of Konva nodes that should appear on the print
 * (typically the content layer's `wn-obj` children — i.e. excluding the
 * transformer, the seam guides and the rubber-band selection rect).
 *
 * ── Two things this file gets right that the first version did not ──
 *
 * 1. It never asks the printer to print to the paper edge. Tiles are placed at
 *    `margin_mm` from every side. A full-bleed page is either clipped by the
 *    driver or silently shrunk by "fit to page", and both break a tiled poster
 *    — see the long note in geometry.js.
 *
 * 2. The glue overlap carries REAL duplicated pixels. Neighbouring tiles are
 *    cut with a stride shorter than their width, so the same strip of artwork
 *    is printed on both sheets; laying one on top of the other and matching
 *    the picture makes the seam vanish. Previously "overlap" drew a dashed
 *    line and nothing else, so gluing along it hid content.
 *
 * ── Memory ──
 *
 * Tiles are rendered one at a time on a sheet-sized off-screen stage
 * (~8.7 MP for A4 at 300 DPI) instead of rendering the whole poster into one
 * giant canvas first. A 4×3 board used to allocate a single 104 MP canvas —
 * ~420 MB of backing store, which iOS Safari refuses outright and Android
 * Chrome often fails to allocate mid-export, losing the whole job. Per-tile
 * rendering keeps peak use flat no matter how big the board is.
 */
(function () {
    'use strict';

    const PX_PER_MM = 300 / 25.4;

    function geom(project) {
        return window.WNGeom.layoutFromCanvas(project.canvas);
    }

    // ─── jsPDF lazy-load ──────────────────────────────────────────────
    let _jspdfPromise = null;
    function loadJsPDF() {
        if (window.jspdf?.jsPDF) return Promise.resolve(window.jspdf.jsPDF);
        if (_jspdfPromise) return _jspdfPromise;
        _jspdfPromise = new Promise((resolve, reject) => {
            const tryUrl = (url, next) => {
                const s = document.createElement('script');
                s.src = url;
                s.onload = () => resolve(window.jspdf.jsPDF);
                s.onerror = () => {
                    if (next) next();
                    else reject(new Error('Could not load jsPDF from any source'));
                };
                document.head.appendChild(s);
            };
            tryUrl('/vendor/jspdf/jspdf.umd.min.js', () =>
                tryUrl('/cdn/jsdelivr/npm/jspdf@2.5.2/dist/jspdf.umd.min.js', null));
        });
        return _jspdfPromise;
    }

    function loadJSZip() {
        if (window.JSZip) return Promise.resolve(window.JSZip);
        return new Promise((resolve, reject) => {
            const tryUrl = (url, next) => {
                const s = document.createElement('script');
                s.src = url;
                s.onload = () => resolve(window.JSZip);
                s.onerror = () => next ? next() : reject(new Error('Could not load JSZip'));
                document.head.appendChild(s);
            };
            tryUrl('/vendor/jszip/jszip.min.js', () =>
                tryUrl('/cdn/jsdelivr/npm/jszip@3.10.1/dist/jszip.min.js', null));
        });
    }

    // ─── Render exactly one tile at 300 DPI ───────────────────────────
    /**
     * Draw the artwork region belonging to sheet (c, r) onto a canvas the size
     * of that sheet's printable area.
     *
     * The whole poster is never materialised. Instead the off-screen stage is
     * only tile-sized and the content layer is shifted by −tile.x/−tile.y, so
     * Konva clips everything outside the tile for free.
     *
     * `cloneCache` lets a multi-tile job clone each node once per tile without
     * re-decoding images: Konva.clone() copies image nodes by reference, so the
     * same HTMLImageElement is reused across all tiles.
     */
    /**
     * The copy of an element that goes on paper.
     *
     * An empty photo slot is half scaffolding, half artwork. The dashed
     * outline belongs on the sheet — a printed wall newspaper with marked
     * boxes is how a class glues real photographs in afterwards. The 🖼 glyph
     * and the words "Add a photo" do NOT: they are interface, and interface
     * must never reach the wall. Templates now ship three slots each, so this
     * stopped being a thing a user does deliberately once and started being
     * the default state of a fresh board.
     */
    function printClone(node) {
        const copy = node.clone({ draggable: false });
        if (node.getAttr && node.getAttr('elType') === 'frame' && copy.find) {
            copy.find('.wn-frame-glyph').forEach((n) => n.destroy());
            copy.find('.wn-frame-label').forEach((n) => n.destroy());
        }
        return copy;
    }

    function renderTile(project, contentNodes, c, r, dpi) {
        const Konva = window.Konva;
        const L = geom(project);
        const rect = L.tileRectPX(c, r);
        // The board's coordinate space is fixed at 300 dpi; a lower export dpi
        // is a straight downscale of the same geometry, so the tiles still
        // register against each other exactly.
        const ratio = dpi ? Math.max(0.2, Math.min(1, dpi / L.dpi)) : 1;

        const container = document.createElement('div');
        container.style.cssText = 'position:absolute;left:-99999px;top:-99999px;pointer-events:none;';
        container.style.width = rect.w + 'px';
        container.style.height = rect.h + 'px';
        document.body.appendChild(container);

        try {
            const stage = new Konva.Stage({ container, width: rect.w, height: rect.h });

            const bg = new Konva.Layer({ listening: false });
            bg.add(new Konva.Rect({
                x: 0, y: 0, width: rect.w, height: rect.h,
                fill: project.canvas.background?.color || '#ffffff',
                listening: false,
            }));

            // Shift the world so this tile's top-left sits at the stage origin.
            const content = new Konva.Layer({ listening: false, x: -rect.x, y: -rect.y });
            for (const node of contentNodes) {
                if (!node.hasName?.('wn-obj')) continue;
                content.add(printClone(node));
            }

            stage.add(bg);
            stage.add(content);
            stage.draw();

            const canvas = stage.toCanvas({ pixelRatio: ratio });
            stage.destroy();
            return canvas;
        } finally {
            container.remove();
        }
    }

    // ─── Page furniture ───────────────────────────────────────────────
    /**
     * Desaturate a rendered tile in place.
     *
     * Rec. 709 luma rather than a flat channel average: an average turns a red
     * heading and a blue one into the same grey, which is exactly the case a
     * wall newspaper is full of.
     */
    function toGrayscale(canvas) {
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        const w = canvas.width, h = canvas.height;
        if (!w || !h) return canvas;
        const img = ctx.getImageData(0, 0, w, h);
        const d = img.data;
        for (let i = 0; i < d.length; i += 4) {
            const y = (d[i] * 0.2126 + d[i + 1] * 0.7152 + d[i + 2] * 0.0722) | 0;
            d[i] = d[i + 1] = d[i + 2] = y;
        }
        ctx.putImageData(img, 0, 0);
        return canvas;
    }


    /**
     * Draw a string onto the PDF as a small raster image.
     *
     * jsPDF's built-in fonts are WinAnsi: they cannot render Cyrillic, Korean,
     * Arabic or even the arrows "←→" this page wants. Handing them a Russian
     * caption produces mojibake on the paper, which is worse than no caption.
     * Rendering the string on a canvas first costs a few kilobytes per page
     * and makes every locale print correctly with no embedded font.
     *
     * `align` positions the box: 'left' | 'center' | 'right' around x_mm.
     */
    function pdfText(pdf, text, x_mm, y_mm, h_mm, color, align, angle) {
        if (!text) return;
        const scale = 12; // px per mm — well above 300 DPI for crisp small type
        const fontPx = Math.max(8, Math.round(h_mm * scale));
        const probe = document.createElement('canvas').getContext('2d');
        probe.font = fontPx + 'px Inter, Arial, sans-serif';
        const wpx = Math.ceil(probe.measureText(text).width) + 4;
        const hpx = Math.ceil(fontPx * 1.35);

        const cv = document.createElement('canvas');
        cv.width = wpx; cv.height = hpx;
        const ctx = cv.getContext('2d');
        ctx.font = fontPx + 'px Inter, Arial, sans-serif';
        ctx.fillStyle = color || '#888';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, 2, hpx / 2);

        const w_mm = wpx / scale;
        const box_h_mm = hpx / scale;
        let x = x_mm;
        if (align === 'center') x = x_mm - w_mm / 2;
        else if (align === 'right') x = x_mm - w_mm;

        if (angle) {
            pdf.addImage(cv.toDataURL('image/png'), 'PNG', x, y_mm, w_mm, box_h_mm, undefined, 'FAST', angle);
        } else {
            pdf.addImage(cv.toDataURL('image/png'), 'PNG', x, y_mm, w_mm, box_h_mm, undefined, 'FAST');
        }
        cv.width = cv.height = 0;
    }

    /**
     * Crop marks sit ON the artwork corners, i.e. exactly at the margin, so a
     * user who prefers to trim rather than overlap cuts along the true edge of
     * the printed area. They are drawn in the blank margin itself and never
     * cover artwork.
     */
    function drawCropMarks(pdf, m, aw, ah) {
        pdf.setLineWidth(0.2);
        pdf.setDrawColor(150);
        const len = Math.min(4, m);
        if (len <= 0.5) return;
        const x0 = m, y0 = m, x1 = m + aw, y1 = m + ah;
        // Ticks pointing outward from each artwork corner.
        pdf.line(x0 - len, y0, x0, y0);   pdf.line(x0, y0 - len, x0, y0);
        pdf.line(x1, y0, x1 + len, y0);   pdf.line(x1, y0 - len, x1, y0);
        pdf.line(x0 - len, y1, x0, y1);   pdf.line(x0, y1, x0, y1 + len);
        pdf.line(x1, y1, x1 + len, y1);   pdf.line(x1, y1, x1, y1 + len);
    }

    function drawSheetLabel(pdf, c, r, cols, rows, m, aw, ah, i18n, L) {
        // Separate pages are named, not addressed: a printed page that says
        // "График дежурства" can be matched to its pocket months later, and
        // "B2" cannot. Neighbour arrows are meaningless for them — there is
        // nothing to line them up with.
        if (L && L.mode === 'pages') {
            const name = (i18n.page_names && i18n.page_names[L.pageIndex(c, r)]) || '';
            if (name) pdfText(pdf, name, Math.max(0.8, m - 3.6), Math.max(0.4, m - 4.2), 3.2, '#6b7280', 'left');
            return;
        }

        // Sheet id sits in the top-left margin — outside the artwork, so it
        // never has to be trimmed off the picture.
        pdfText(pdf, window.WNGeom.sheetLabel(c, r),
            Math.max(0.8, m - 3.6), Math.max(0.4, m - 4.2), 3.2, '#6b7280', 'left');

        const hints = [];
        if (c > 0)        hints.push('← ' + window.WNGeom.sheetLabel(c - 1, r));
        if (c < cols - 1) hints.push(window.WNGeom.sheetLabel(c + 1, r) + ' →');
        if (r > 0)        hints.push('↑ ' + window.WNGeom.sheetLabel(c, r - 1));
        if (r < rows - 1) hints.push('↓ ' + window.WNGeom.sheetLabel(c, r + 1));
        if (!hints.length) return;
        pdfText(pdf, hints.join('    '), m + aw / 2, m + ah + 0.6, 2.4, '#9aa3ae', 'center');
    }

    /**
     * Mark the glue band. The band is the strip of artwork this sheet shares
     * with its right/bottom neighbour: the two sheets carry identical pixels
     * there, so the user overlaps them and slides until the picture matches.
     */
    function drawGlueBand(pdf, c, r, cols, rows, m, aw, ah, overlap_mm, i18n) {
        if (overlap_mm <= 0) return;
        pdf.setDrawColor(90, 150, 220);
        pdf.setLineDashPattern([1.6, 1.6], 0);
        pdf.setLineWidth(0.25);
        if (c < cols - 1) pdf.line(m + aw - overlap_mm, m, m + aw - overlap_mm, m + ah);
        if (r < rows - 1) pdf.line(m, m + ah - overlap_mm, m + aw, m + ah - overlap_mm);
        pdf.setLineDashPattern([], 0);

        const caption = i18n.glue_band_caption || 'glue: overlap here';
        if (c < cols - 1) {
            pdfText(pdf, caption, m + aw - overlap_mm - 3.4, m + ah / 2, 2.2, '#5a96dc', 'left', 90);
        } else if (r < rows - 1) {
            pdfText(pdf, caption, m + 1.5, m + ah - overlap_mm - 3.4, 2.2, '#5a96dc', 'left');
        }
    }

    /**
     * A 100 mm ruler in the bottom margin of the first sheet.
     *
     * This exists because the single most common way a tiled poster fails is
     * invisible: the print dialog defaults to "fit to page" / "shrink to fit"
     * and quietly scales every sheet by a few percent. Nothing on screen says
     * so, and the user only finds out after printing and cutting all of them.
     * Measuring this line with any ruler answers the question in two seconds —
     * 100 mm means the scale was honest, anything else means reprint with
     * scaling off.
     */
    function drawCalibrationRuler(pdf, m, aw, ah, i18n) {
        const len = 100;
        if (aw < len + 4) return;
        const y = m + ah + Math.min(m, 6) - 3.2;
        const x = m;
        pdf.setDrawColor(60);
        pdf.setLineWidth(0.3);
        pdf.line(x, y, x + len, y);
        for (let i = 0; i <= 10; i++) {
            const tick = i % 5 === 0 ? 1.8 : 1;
            pdf.line(x + i * 10, y - tick, x + i * 10, y + tick);
        }
        pdfText(pdf, i18n.ruler_caption || '100 mm — print at 100%, no scaling',
            x + len + 2, y - 1.4, 2.4, '#3f3f46', 'left');
    }

    /**
     * Registration crosses inside the glue band.
     *
     * These are the marks you actually align by. Matching the paper EDGES only
     * works if the printer reproduced the page at exactly 100%; matching two
     * printed crosses works even when it did not, because a uniform shrink
     * moves both sheets' crosses by the same amount and the artwork with them.
     * Epson's own poster driver ships "overlapping alignment marks" for this
     * reason, and it is the difference between "the sheets do not line up" and
     * "the sheets line up, the poster is 4% smaller".
     *
     * Returns positions in SHEET-LOCAL mm (already offset by the margin).
     */
    function registrationMarks(L, c, r) {
        if (L.overlap_mm <= 0) return [];
        const rect = L.tileRectMM(c, r);
        const out = [];
        const inX = (x) => x >= rect.x - 0.01 && x <= rect.x + rect.w + 0.01;
        const inY = (y) => y >= rect.y - 0.01 && y <= rect.y + rect.h + 0.01;

        // Vertical seams: a column of three crosses down the middle of the band.
        for (let i = 1; i < L.cols; i++) {
            const cx = i * L.stride_w_mm + L.overlap_mm / 2;
            if (!inX(cx)) continue;
            for (const f of [0.22, 0.5, 0.78]) {
                const cy = rect.y + rect.h * f;
                out.push({ x: L.margin_mm + (cx - rect.x), y: L.margin_mm + (cy - rect.y) });
            }
        }
        // Horizontal seams: a row of three across the band.
        for (let i = 1; i < L.rows; i++) {
            const cy = i * L.stride_h_mm + L.overlap_mm / 2;
            if (!inY(cy)) continue;
            for (const f of [0.22, 0.5, 0.78]) {
                const cx = rect.x + rect.w * f;
                out.push({ x: L.margin_mm + (cx - rect.x), y: L.margin_mm + (cy - rect.y) });
            }
        }
        return out;
    }

    function drawRegistrationMarks(pdf, L, c, r) {
        const marks = registrationMarks(L, c, r);
        if (!marks.length) return;
        pdf.setDrawColor(40, 110, 200);
        pdf.setLineWidth(0.25);
        const arm = 2.2;
        for (const m of marks) {
            pdf.line(m.x - arm, m.y, m.x + arm, m.y);
            pdf.line(m.x, m.y - arm, m.x, m.y + arm);
            pdf.circle(m.x, m.y, 1.1, 'S');
        }
    }

    function sleep(ms) { return new Promise((r) => setTimeout(r, ms)); }

    function defaults(options) {
        return Object.assign({
            withCropMarks: true,
            withLabels: true,
            withOverlap: true,
            withRuler: true,
            withMarks: true,
            /* Output resolution. A wall newspaper is read from about a metre,
               where 150 dpi is already indistinguishable from 300 — and 300
               quadruples the PDF, which matters on school Wi-Fi and on the
               phone the teacher is printing from. 300 stays available for
               anyone putting small print on it. */
            dpi: 200,
            jpegQuality: 0.9,
            /* Print in black and white.
               School printers are the constraint here, not taste: a colour
               cartridge is a budget line and a wall newspaper printed on the
               office laser has to survive it. Russian sellers of ready-made
               стенгазеты ship every design as a colour/greyscale pair for
               exactly this reason. Converting here — rather than telling the
               user to tick "greyscale" in the print dialog — means the file
               itself is honest and the preview shows what will come out. */
            grayscale: false,
            /* null = every sheet. Otherwise a Set of "c,r" keys — this is how
               a single smudged or jammed sheet gets reprinted without running
               the other eleven again. */
            sheets: null,
            i18n: {},
        }, options || {});
    }

    function wantsSheet(opts, c, r) {
        return !opts.sheets || opts.sheets.has(c + ',' + r);
    }

    /** Ordered list of the sheets an export will actually emit. */
    function sheetList(L, opts) {
        const out = [];
        for (let r = 0; r < L.rows; r++) {
            for (let c = 0; c < L.cols; c++) {
                // A grid cell with no page behind it (seven pockets in a 4×2
                // grid leaves one) must not print a blank sheet.
                if (L.pageExists && !L.pageExists(c, r)) continue;
                if (wantsSheet(opts, c, r)) out.push({ c, r });
            }
        }
        return out;
    }

    // ─── PDF export ───────────────────────────────────────────────────
    async function exportPDF({ project, contentNodes, options, onProgress }) {
        const opts = defaults(options);
        const jsPDF = await loadJsPDF();
        const L = geom(project);
        const { cols, rows } = L;

        const pdf = new jsPDF({
            unit: 'mm',
            format: [L.sheet_w_mm, L.sheet_h_mm],
            orientation: L.sheet_w_mm >= L.sheet_h_mm ? 'landscape' : 'portrait',
            compress: true,
        });

        const list = sheetList(L, opts);
        const total = list.length;
        let done = 0;

        for (const { c, r } of list) {
            if (done > 0) pdf.addPage([L.sheet_w_mm, L.sheet_h_mm]);

            const tile = renderTile(project, contentNodes, c, r, opts.dpi);
            if (opts.grayscale) toGrayscale(tile);
            const dataUrl = tile.toDataURL('image/jpeg', opts.jpegQuality);
            // Artwork inset by the margin — the printer's non-printable border
            // falls on blank paper instead of on the picture.
            pdf.addImage(dataUrl, 'JPEG', L.margin_mm, L.margin_mm, L.usable_w_mm, L.usable_h_mm);

            if (opts.withOverlap) {
                drawGlueBand(pdf, c, r, cols, rows, L.margin_mm, L.usable_w_mm, L.usable_h_mm, L.overlap_mm, opts.i18n);
            }
            if (opts.withMarks)     drawRegistrationMarks(pdf, L, c, r);
            if (opts.withCropMarks) drawCropMarks(pdf, L.margin_mm, L.usable_w_mm, L.usable_h_mm);
            if (opts.withLabels)    drawSheetLabel(pdf, c, r, cols, rows, L.margin_mm, L.usable_w_mm, L.usable_h_mm, opts.i18n, L);
            // The scale instruction and the ruler go on whichever sheet comes
            // out first — pinning them to A1 would hide them on a reprint of a
            // single middle sheet, which is exactly when scale goes wrong.
            if (opts.withRuler && done === 0) {
                drawCalibrationRuler(pdf, L.margin_mm, L.usable_w_mm, L.usable_h_mm, opts.i18n);
            }

            // Release the tile's backing store before the next one is made.
            tile.width = tile.height = 0;

            done++;
            if (onProgress) onProgress(done, total);
            await sleep(0);
        }

        return pdf.output('blob');
    }

    // ─── PNG-ZIP export ───────────────────────────────────────────────
    async function exportPNGZip({ project, contentNodes, options, onProgress }) {
        const opts = defaults(options);
        const JSZip = await loadJSZip();
        const L = geom(project);
        const { cols, rows } = L;

        const zip = new JSZip();
        const list = sheetList(L, opts);
        const total = list.length;
        let done = 0;

        for (const { c, r } of list) {
            const tile = renderTile(project, contentNodes, c, r, opts.dpi);
            if (opts.grayscale) toGrayscale(tile);
            const sheet = paperizeTile(tile, L, c, r, opts);
            const blob = await new Promise((resolve) => sheet.toBlob(resolve, 'image/png'));
            const base = (L.mode === 'pages')
                ? String(L.pageIndex(c, r) + 1).padStart(2, '0')
                : window.WNGeom.sheetLabel(c, r);
            zip.file(`${base}.png`, blob);
            tile.width = tile.height = 0;
            sheet.width = sheet.height = 0;
            done++;
            if (onProgress) onProgress(done, total);
            await sleep(0);
        }

        return zip.generateAsync({
            type: 'blob',
            compression: 'DEFLATE',
            compressionOptions: { level: 6 },
        });
    }

    /**
     * Put a rendered tile onto a full sheet-sized white page with the same
     * margin and furniture the PDF gets, so a printed PNG lines up with a
     * printed PDF page for page. Without this the PNG route would quietly be
     * the old full-bleed output and the two exports would disagree.
     */
    function paperizeTile(tile, L, c, r, opts) {
        // Derive px-per-mm from the tile we were actually handed, so a 150 dpi
        // export lays its furniture at 150 dpi too instead of drawing 300 dpi
        // marks around a half-size picture.
        const ppmm = tile.width / L.usable_w_mm;
        const out = document.createElement('canvas');
        out.width = Math.round(L.sheet_w_mm * ppmm);
        out.height = Math.round(L.sheet_h_mm * ppmm);
        const ctx = out.getContext('2d');
        const m = Math.round(L.margin_mm * ppmm);

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, out.width, out.height);
        ctx.drawImage(tile, m, m);

        const aw = tile.width, ah = tile.height;
        if (opts.withCropMarks && m > 6) {
            ctx.strokeStyle = '#999';
            ctx.lineWidth = Math.max(2, Math.round(0.2 * ppmm));
            const len = Math.min(Math.round(4 * ppmm), m);
            const x0 = m, y0 = m, x1 = m + aw, y1 = m + ah;
            ctx.beginPath();
            ctx.moveTo(x0 - len, y0); ctx.lineTo(x0, y0);
            ctx.moveTo(x0, y0 - len); ctx.lineTo(x0, y0);
            ctx.moveTo(x1, y0); ctx.lineTo(x1 + len, y0);
            ctx.moveTo(x1, y0 - len); ctx.lineTo(x1, y0);
            ctx.moveTo(x0 - len, y1); ctx.lineTo(x0, y1);
            ctx.moveTo(x0, y1); ctx.lineTo(x0, y1 + len);
            ctx.moveTo(x1, y1); ctx.lineTo(x1 + len, y1);
            ctx.moveTo(x1, y1); ctx.lineTo(x1, y1 + len);
            ctx.stroke();
        }

        if (opts.withOverlap && L.overlap_mm > 0) {
            const ov = Math.round(L.overlap_mm * ppmm);
            ctx.save();
            ctx.strokeStyle = 'rgba(90,150,220,.9)';
            ctx.lineWidth = Math.max(2, Math.round(0.25 * ppmm));
            ctx.setLineDash([Math.round(1.6 * ppmm), Math.round(1.6 * ppmm)]);
            ctx.beginPath();
            if (c < L.cols - 1) { ctx.moveTo(m + aw - ov, m); ctx.lineTo(m + aw - ov, m + ah); }
            if (r < L.rows - 1) { ctx.moveTo(m, m + ah - ov); ctx.lineTo(m + aw, m + ah - ov); }
            ctx.stroke();
            ctx.restore();
        }

        // Registration crosses, in the same places the PDF puts them.
        if (opts.withMarks) {
            ctx.save();
            ctx.strokeStyle = 'rgba(40,110,200,.95)';
            ctx.lineWidth = Math.max(2, Math.round(0.25 * ppmm));
            const arm = 2.2 * ppmm;
            for (const mk of registrationMarks(L, c, r)) {
                const x = mk.x * ppmm, y = mk.y * ppmm;
                ctx.beginPath();
                ctx.moveTo(x - arm, y); ctx.lineTo(x + arm, y);
                ctx.moveTo(x, y - arm); ctx.lineTo(x, y + arm);
                ctx.stroke();
                ctx.beginPath();
                ctx.arc(x, y, 1.1 * ppmm, 0, Math.PI * 2);
                ctx.stroke();
            }
            ctx.restore();
        }

        if (opts.withLabels) {
            ctx.fillStyle = '#888';
            ctx.font = `bold ${Math.round(4 * ppmm)}px Inter, sans-serif`;
            const label = (L.mode === 'pages')
                ? ((opts.i18n?.page_names && opts.i18n.page_names[L.pageIndex(c, r)]) || '')
                : window.WNGeom.sheetLabel(c, r);
            if (label) ctx.fillText(label, Math.max(4, m - Math.round(3.2 * ppmm)), Math.max(m - Math.round(1.4 * ppmm), Math.round(4 * ppmm)));
            // Separate pages have no neighbours to point at.
            if (L.mode === 'pages') return out;

            const hints = [];
            if (c > 0)            hints.push('← ' + window.WNGeom.sheetLabel(c - 1, r));
            if (c < L.cols - 1)   hints.push(window.WNGeom.sheetLabel(c + 1, r) + ' →');
            if (r > 0)            hints.push('↑ ' + window.WNGeom.sheetLabel(c, r - 1));
            if (r < L.rows - 1)   hints.push('↓ ' + window.WNGeom.sheetLabel(c, r + 1));
            if (hints.length) {
                ctx.font = `${Math.round(2.6 * ppmm)}px Inter, sans-serif`;
                ctx.textAlign = 'center';
                ctx.fillText(hints.join('   '), out.width / 2, m + ah + Math.min(m, Math.round(4 * ppmm)) - Math.round(0.6 * ppmm));
                ctx.textAlign = 'start';
            }
        }
        return out;
    }

    // ─── Tile preview (for the export modal) ──────────────────────────
    /**
     * A downscaled preview of one sheet exactly as it will print — white
     * paper, margin, artwork, furniture. Renders only the requested tile, so
     * clicking through a 12-sheet board costs one tile each time instead of
     * re-rendering the entire poster per click, which is what it used to do.
     */
    async function tilePreviewURL({ project, contentNodes, row, col, options, maxW }) {
        const cap = maxW || 620;
        const opts = defaults(options);
        const L = geom(project);
        const c = col || 0, r = row || 0;

        // The preview is downscaled to a few hundred pixels anyway, so render
        // it small: a full-resolution render per click is what made stepping
        // through a 12-sheet board feel broken.
        const tile = renderTile(project, contentNodes, c, r, 96);
        if (opts.grayscale) toGrayscale(tile);
        const sheet = paperizeTile(tile, L, c, r, opts);
        tile.width = tile.height = 0;

        const ratio = sheet.width / sheet.height;
        const w = Math.min(cap, sheet.width);
        const h = Math.round(w / ratio);
        const out = document.createElement('canvas');
        out.width = w;
        out.height = h;
        const octx = out.getContext('2d');
        octx.imageSmoothingQuality = 'high';
        octx.drawImage(sheet, 0, 0, w, h);
        sheet.width = sheet.height = 0;
        return out.toDataURL('image/jpeg', 0.85);
    }

    /**
     * One PDF page the size of the whole assembled poster.
     *
     * This is the "print shop / plotter" half of what Russian sellers of
     * ready-made стенгазеты actually ship — every design comes as a pair: cut
     * into A4 for the office printer, and one large sheet for anyone with
     * access to a wide-format machine. A teacher with a print shop nearby
     * should not have to glue twelve pieces of paper.
     *
     * Resolution is capped by total pixels rather than fixed at the export
     * dpi: a 4×3 board at 300 dpi is a 104 MP canvas, which is the allocation
     * the tiled path was rewritten to avoid. The cap costs nothing that shows
     * on a poster read from a metre away, and the effective dpi is reported
     * back so the UI can be honest about it.
     */
    async function exportSinglePDF({ project, contentNodes, options, onProgress }) {
        const opts = defaults(options);
        const jsPDF = await loadJsPDF();
        const L = geom(project);

        const MAX_PX = 30e6;
        const wanted = Math.max(0.2, Math.min(1, opts.dpi / L.dpi));
        const full = L.width_px * L.height_px;
        const ratio = Math.min(wanted, Math.sqrt(MAX_PX / Math.max(1, full)));
        const effDpi = Math.round(L.dpi * ratio);

        if (onProgress) onProgress(0, 1);

        const Konva = window.Konva;
        const container = document.createElement('div');
        container.style.cssText = 'position:absolute;left:-99999px;top:-99999px;pointer-events:none;';
        document.body.appendChild(container);
        let canvas;
        try {
            const stage = new Konva.Stage({ container, width: L.width_px, height: L.height_px });
            const bg = new Konva.Layer({ listening: false });
            // A pages board has no single sheet to export — this path is only
            // offered for glued posters, so one background is correct here.
            bg.add(new Konva.Rect({
                x: 0, y: 0, width: L.width_px, height: L.height_px,
                fill: project.canvas.background?.color || '#ffffff', listening: false,
            }));
            const content = new Konva.Layer({ listening: false });
            for (const node of contentNodes) {
                if (!node.hasName?.('wn-obj')) continue;
                content.add(printClone(node));
            }
            stage.add(bg);
            stage.add(content);
            stage.draw();
            canvas = stage.toCanvas({ pixelRatio: ratio });
            stage.destroy();
        } finally {
            container.remove();
        }

        if (opts.grayscale) toGrayscale(canvas);

        const pdf = new jsPDF({
            unit: 'mm',
            format: [L.poster_w_mm, L.poster_h_mm],
            orientation: L.poster_w_mm >= L.poster_h_mm ? 'landscape' : 'portrait',
            compress: true,
        });
        pdf.addImage(canvas.toDataURL('image/jpeg', opts.jpegQuality), 'JPEG',
            0, 0, L.poster_w_mm, L.poster_h_mm);
        canvas.width = canvas.height = 0;

        if (onProgress) onProgress(1, 1);
        const blob = pdf.output('blob');
        blob.effDpi = effDpi;
        return blob;
    }

    // ─── Public API ───────────────────────────────────────────────────
    window.WNPrinting = {
        exportPDF,
        exportSinglePDF,
        exportPNGZip,
        tilePreviewURL,
        renderTile,
        registrationMarks,
    };
})();
